<script>
/**
 * @component PharmacyDutyAdmin
 * @description UC-REG-296 — Publicación de la nómina diaria de farmacias de
 *              guardia (admin, Campana). Elige un día, marca qué farmacias
 *              del maestro están de turno (24 h) o abiertas con horario,
 *              agrega un aviso opcional y publica. También quita la nómina
 *              del día y enlaza al ABM de farmacias.
 * @example <PharmacyDutyAdmin />
 */
import pharmacyService from "@/services/pharmacy";
import pharmacyDutyService from "@/services/pharmacy-duty";
import {
  PHARMACY_DUTY_NOTICE,
  formatDutyDate,
  todayDutyDate,
} from "@/services/pharmacy-duty-view";

/** @returns {Object} fila editable de una farmacia */
const emptyRow = (pharmacy) => ({
  pharmacy,
  included: false,
  onDuty: true,
  hours: "",
});

export default {
  name: "PharmacyDutyAdmin",

  data() {
    return {
      notice: PHARMACY_DUTY_NOTICE,
      /** @type {string} día seleccionado `AAAA-MM-DD` */
      date: todayDutyDate(),
      /** @type {Array<Object>} farmacias activas del maestro */
      pharmacies: [],
      /** @type {Array<Object>} filas editables (una por farmacia) */
      rows: [],
      notes: "",
      /** @type {boolean} si el día ya tiene nómina publicada */
      isPublished: false,
      updatedAt: null,
      isLoading: true,
      isSaving: false,
    };
  },

  computed: {
    /** @returns {string} */
    dateLabel() {
      return formatDutyDate(this.date);
    },

    /** @returns {Array<Object>} filas incluidas en la nómina */
    includedRows() {
      return this.rows.filter((r) => r.included);
    },

    /** @returns {number} */
    onDutyCount() {
      return this.includedRows.filter((r) => r.onDuty).length;
    },
  },

  created() {
    this.load();
  },

  methods: {
    /** @description Carga el maestro de farmacias y la nómina del día. */
    async load() {
      this.isLoading = true;
      try {
        if (!this.pharmacies.length) {
          const result = await pharmacyService.query({
            isActive: true,
            limit: 200,
            offset: 0,
          });
          this.pharmacies = (result && result.docs) || [];
        }
        const duty = await pharmacyDutyService.getToday(this.date);
        this.applyDuty(duty);
      } catch (err) {
        this.applyDuty(null);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Arma las filas a partir del maestro y la nómina del día.
     * @param {Object|null} duty - respuesta de GET /pharmacy-duty/today
     */
    applyDuty(duty) {
      const byId = new Map(
        ((duty && duty.entries) || []).map((e) => [String(e.pharmacy._id), e])
      );
      this.rows = this.pharmacies.map((p) => {
        const row = emptyRow(p);
        const entry = byId.get(String(p._id));
        if (entry) {
          row.included = true;
          row.onDuty = entry.onDuty !== false;
          row.hours = entry.hours || "";
        }
        return row;
      });
      this.isPublished = Boolean(duty && duty.published);
      this.notes = (duty && duty.notes) || "";
      this.updatedAt = (duty && duty.updatedAt) || null;
    },

    /** @description Cambio de fecha desde el date picker. */
    onDateChange() {
      if (!this.date) this.date = todayDutyDate();
      this.load();
    },

    /**
     * @description Payload de PUT /pharmacy-duty/:date.
     * @returns {{entries: Array, notes: string}}
     */
    buildPayload() {
      return {
        entries: this.includedRows.map((r) => ({
          pharmacy: r.pharmacy._id,
          onDuty: r.onDuty,
          hours: r.hours.trim() || undefined,
        })),
        notes: this.notes.trim(),
      };
    },

    /** @description Publica la nómina del día. */
    async publish() {
      this.isSaving = true;
      try {
        const duty = await pharmacyDutyService.publish(
          this.date,
          this.buildPayload()
        );
        this.applyDuty(duty);
        this.$message.success(`Nómina del ${this.dateLabel} publicada.`);
      } catch (err) {
        // mensaje mostrado por la capa HTTP
      } finally {
        this.isSaving = false;
      }
    },

    /** @description Confirma y quita la nómina del día. */
    confirmRemove() {
      this.$confirm(
        `¿Querés quitar la nómina publicada del ${this.dateLabel}? Los vecinos dejarán de verla.`,
        "Quitar nómina",
        {
          confirmButtonText: "Quitar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => this.remove())
        .catch(() => {});
    },

    /** @description Quita la nómina del día. */
    async remove() {
      this.isSaving = true;
      try {
        await pharmacyDutyService.remove(this.date);
        this.applyDuty(null);
        this.$message.success("Nómina quitada.");
      } catch (err) {
        // mensaje mostrado por la capa HTTP
      } finally {
        this.isSaving = false;
      }
    },

    /** @description Deselecciona todas las farmacias. */
    clearAll() {
      this.rows.forEach((r) => {
        r.included = false;
      });
    },
  },
};
</script>

<template lang="pug">
.pda
  .pda__toolbar
    .pda__toolbar-left
      label.pda__label(for="pda-date") Día
      el-date-picker#pda-date(
        v-model="date"
        type="date"
        value-format="yyyy-MM-dd"
        format="dd/MM/yyyy"
        size="small"
        :clearable="false"
        @change="onDateChange"
      )
      span.pda__date-label {{ dateLabel }}
      span.pda__status(:class="{ 'pda__status--on': isPublished }") {{ isPublished ? 'Publicada' : 'Sin publicar' }}
    .pda__toolbar-right
      router-link.pda__link(:to="{ name: 'pharmacies' }")
        micon(name="local_pharmacy")
        span Administrar farmacias

  p.pda__hint Marcá las farmacias que están de turno (24 h) o abiertas y su horario. El vecino ve el aviso: "{{ notice }}"

  div(v-loading="isLoading")
    .pda__empty(v-if="!isLoading && !pharmacies.length")
      p No hay farmacias cargadas en el maestro.
      router-link(:to="{ name: 'pharmacies' }") Cargar farmacias

    table.pda__table(v-if="rows.length")
      thead
        tr
          th Incluir
          th Farmacia
          th Estado
          th Horario visible
      tbody
        tr.pda__row(v-for="r in rows" :key="r.pharmacy._id" :class="{ 'is-included': r.included }")
          td
            el-checkbox(v-model="r.included")
          td
            .pda__pharmacy
              span.pda__pharmacy-name {{ r.pharmacy.commercialName }}
              span.pda__pharmacy-sub {{ [r.pharmacy.address, r.pharmacy.phone].filter(Boolean).join(' · ') }}
          td
            el-radio-group(v-model="r.onDuty" size="mini" :disabled="!r.included")
              el-radio-button(:label="true") De turno
              el-radio-button(:label="false") Abierta
          td
            el-input(
              v-model="r.hours"
              size="small"
              :disabled="!r.included"
              :placeholder="r.onDuty ? '24 h (opcional)' : 'Ej: 8 a 22 h'"
              maxlength="60"
            )

    .pda__notes
      label.pda__label(for="pda-notes") Aviso del día (opcional)
      el-input#pda-notes(
        v-model="notes"
        type="textarea"
        :rows="2"
        maxlength="300"
        placeholder="Ej: Feriado: solo atiende la farmacia de turno."
      )

    .pda__footer
      span.pda__summary {{ includedRows.length }} farmacias en la nómina · {{ onDutyCount }} de turno
      .pda__actions
        el-button(size="small" @click="clearAll" :disabled="!includedRows.length") Limpiar
        el-button(v-if="isPublished" size="small" type="danger" plain :loading="isSaving" @click="confirmRemove") Quitar nómina
        el-button(size="small" type="primary" :loading="isSaving" :disabled="!pharmacies.length" @click="publish") {{ isPublished ? 'Actualizar nómina' : 'Publicar nómina' }}
</template>

<style lang="scss" scoped>
.pda {
  max-width: 960px;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  &__toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__label {
    font-size: 12px;
    font-weight: 700;
    color: $navy;
  }

  &__date-label {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
  }

  &__status {
    display: inline-flex;
    padding: 2px 10px;
    border-radius: 10px;
    background: #f3f4f6;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;

    &--on {
      background: #dcfce7;
      color: #166534;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--org-color-primary, $primary);
    text-decoration: none;

    .micon {
      width: 16px;
      height: 16px;
      fill: var(--org-color-primary, $primary);
    }
  }

  &__hint {
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 14px;
  }

  &__empty {
    padding: 24px;
    text-align: center;
    border: 1px dashed #d1d5db;
    border-radius: 10px;
    color: #6b7280;
    font-size: 13px;
    margin-bottom: 16px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 16px;

    th {
      text-align: left;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: #6b7280;
      padding: 10px 12px;
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
    }

    td {
      padding: 10px 12px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
    }
  }

  &__row.is-included td {
    background: #faf5ff;
  }

  &__pharmacy {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__pharmacy-name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
  }

  &__pharmacy-sub {
    font-size: 11px;
    color: #6b7280;
  }

  &__notes {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__summary {
    font-size: 12px;
    color: #6b7280;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
